import classNames from 'classnames';
import * as _ from 'lodash-es';
import type { Transition } from 'motion/react';
import {
  AnimatePresence,
  MotionConfig,
  motion,
  useIsPresent,
} from 'motion/react';
import React, {
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import useAppSelector, { useAppDispatch } from '../../app/redux/useAppSelector';
import EmojiPicker from '../../components/EmojiPicker';
import { MenuItem, MenuSeparator } from '../../components/Menu';
import Tooltip from '../../components/Tooltip';
import { isTouchDevice } from '../../constants/os';
import { useQuill } from '../../editor/QuillContext';
import { EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated';
import { gql } from '../../graphql/generated';
import checkIcon from '../../icons/non-prefilled/16x16/check.svg';
import linkIcon from '../../icons/non-prefilled/16x16/link.svg';
import trashIcon from '../../icons/non-prefilled/16x16/trash.svg';
import { miscToasts, postToasts } from '../../toasts/toasts';
import { useAddRecentlyUsedEmoji } from '../../ui/atoms';
import { UIActions } from '../../ui/uiSlice';
import addTestId from '../../utils/addTestId';
import type { ExtractReactionThreadTypes } from '../../utils/post/threadUtils';
import fullOrgUrl from '../../utils/url/fullOrgUrl';
import postPath from '../../utils/url/postPath';
import reactionThreadHash from '../../utils/url/reactionThreadHash';
import { useOptimisticItemContentRects } from '../useOptimisticItemContentRects';
import useShareSecret from '../useShareSecret';
import MagneticWallItemOverflowMenu, {
  MagneticWallOverflowButton,
} from './MagneticWallItemOverflowMenu';
import * as styles from './ReactionThreadContent.module.scss';
import ThreadReactionList from './ThreadReactionList';
import useResolveThread from './useResolveThread';

const TRANSITION: Transition = {
  type: 'tween',
  ease: [0.25, 0.1, 0.25, 1],
  duration: 0.25,
};

const POST_FRAGMENT = gql(`
  fragment ReactionThreadContent_post on RootPostFields {
    id
    ...postPath_post
  }
`);

gql(`
  fragment ReactionThreadContent_reactions on ReactionFields {
    id
    emoji
    author {
      id
    }
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment ReactionThreadContent_thread on RootThreadFields {
    id
    author {
      id
    }
    ... on ReactionThread {
      reactions {
        ...ReactionThreadContent_reactions
      }
    }
    ... on PublicReactionThread {
      reactions {
        ...ReactionThreadContent_reactions
      }
    }
    ...ThreadReactionList_thread
  }
`);

const REACTION_AVATAR_WIDTH = 34;
const OVERLAP = parseInt(styles.overlap, 10);

interface OverflowMenuProps {
  onResolve?: () => void;
  onCopyLink: () => void;
  onDeleteAllReactions?: () => void;
}

const OverflowMenu = ({
  onResolve,
  onCopyLink,
  onDeleteAllReactions,
}: OverflowMenuProps) => {
  const [isOverflowMenuOpen, setIsOverflowMenuOpen] = useState(false);

  return (
    <MagneticWallItemOverflowMenu
      open={isOverflowMenuOpen}
      onOpenChange={setIsOverflowMenuOpen}
      menuItems={_.compact([
        <MenuItem key="copy" icon={linkIcon} onClick={onCopyLink}>
          Copy link to reactions
        </MenuItem>,
        ...(onDeleteAllReactions
          ? [
              <MenuSeparator key="separator" />,
              <MenuItem
                icon={trashIcon}
                key="delete_thread"
                danger
                onClick={onDeleteAllReactions}
              >
                Delete this thread
              </MenuItem>,
            ]
          : []),
      ])}
    >
      {onResolve ? (
        <Tooltip content="Resolve thread">
          <MagneticWallOverflowButton
            onClick={onResolve}
            icon={checkIcon}
            ariaLabel="Resolve thread"
          />
        </Tooltip>
      ) : null}
    </MagneticWallItemOverflowMenu>
  );
};

type InnerReactionThreadContentProps = {
  thread: ExtractReactionThreadTypes<typeof THREAD_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  expanded: boolean;
  expand: () => void;
  unresolveThread: (threadId: string) => void;
  onResize: () => void;
  layoutId: string;
  asPopOver?: boolean;
  deleteReactionThread: () => void;
} & Pick<
  React.ComponentProps<typeof ThreadReactionList>,
  'deleteReaction' | 'addReaction'
>;

const InnerReactionThreadContent = ({
  thread,
  post,
  expanded,
  unresolveThread,
  expand,
  onResize,
  layoutId,
  asPopOver,
  deleteReactionThread,
  addReaction,
  deleteReaction,
}: InnerReactionThreadContentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const optimisticItemContentRects = useOptimisticItemContentRects();
  const isOptimistic = optimisticItemContentRects.has(thread.id);

  const [isHovered, setIsHovered] = useState(false);
  const [maxItemsShown, setMaxItemsShown] = useState<number | undefined>();

  const currentUser = useAppSelector((state) => state.session.user);
  const readOnly = useAppSelector((state) => state.editor.readOnly);
  const isEditable = useAppSelector((state) => state.editor.editable);
  const shareSecret = useShareSecret();
  const { quill } = useQuill();
  const isPresent = useIsPresent();

  const isAuthor = thread.author.id === currentUser.id;
  const canResolve = isEditable || isAuthor;
  const selectedEmojis = new Set(
    thread.reactions
      .filter((reaction) => reaction.author.id === currentUser.id)
      .map((reaction) => reaction.emoji),
  );

  const dispatch = useAppDispatch();
  const addRecentlyUsedEmoji = useAddRecentlyUsedEmoji();
  const resolveThread = useResolveThread(thread);

  const handleAddReaction = (emoji: string) => {
    addRecentlyUsedEmoji(emoji);
    addReaction(emoji);
  };

  const handleRemoveReaction = (emoji: string) => {
    const reaction = thread.reactions.find(
      (r) => r.author.id === currentUser.id && r.emoji === emoji,
    );
    if (reaction) {
      deleteReaction(reaction.id);
    }
  };

  const handleCopyLink = () => {
    const url = fullOrgUrl(
      postPath(post, { shareSecret, anchor: reactionThreadHash(thread.id) }),
    );

    navigator.clipboard.writeText(url).then(() => {
      dispatch(UIActions.pushToast(miscToasts.linkCopied));
    });
  };

  const handleResolve = () => {
    resolveThread();

    dispatch(
      UIActions.pushToast(
        postToasts.threadResolved({ onUndo: () => unresolveThread(thread.id) }),
      ),
    );
  };

  useLayoutEffect(() => {
    const containerEl = containerRef.current;
    if (containerEl == null) return undefined;

    const handleResize = () => {
      const { width } = containerEl.getBoundingClientRect();
      setMaxItemsShown(Math.round(width / (REACTION_AVATAR_WIDTH - OVERLAP)));
      onResize();
    };
    handleResize();

    const observer = new ResizeObserver(handleResize);
    observer.observe(containerEl);

    return () => {
      observer.disconnect();
    };
  }, [expanded, onResize]);

  useEffect(() => {
    if (isHovered) {
      dispatch(EditorActions.highlightReaction(thread.id));
    } else {
      dispatch(EditorActions.removeMagneticWallHighlight());
    }
  }, [dispatch, thread.id, isHovered]);

  useEffect(() => {
    if (expanded && (readOnly || !quill?.hasFocus())) {
      searchInputRef.current?.focus({ preventScroll: true });
    }
  }, [expanded, readOnly, quill]);

  return (
    <div
      ref={containerRef}
      className={styles.innerContainer}
      {...(isTouchDevice
        ? {}
        : {
            onMouseEnter: () => setIsHovered(true),
            onMouseLeave: () => setIsHovered(false),
          })}
    >
      {expanded && (
        <div key="overflow-menu">
          <OverflowMenu
            onCopyLink={handleCopyLink}
            onResolve={canResolve ? handleResolve : undefined}
            onDeleteAllReactions={isAuthor ? deleteReactionThread : undefined}
          />
        </div>
      )}

      <div className={styles.reactionListContainer}>
        {expanded && <div className={styles.reactionsTitle}>Reactions</div>}
        <motion.div {...(asPopOver ? {} : { layout: 'position', layoutId })}>
          <ThreadReactionList
            expanded={expanded}
            thread={thread}
            showAdditionalNode={!expanded && isHovered}
            onAdditionalNodeClick={expand}
            onContainerClick={!expanded ? expand : undefined}
            maxItemsShown={expanded ? undefined : maxItemsShown}
            spacing="loose"
            hideOverflowCount={!expanded && !isPresent}
            withinOverlay={asPopOver}
            addReaction={addReaction}
            deleteReaction={deleteReaction}
          />
        </motion.div>
      </div>
      {expanded && (
        <div className={styles.emojiPickerContainer}>
          <EmojiPicker
            inline
            fullWidth
            controlsPosition="bottom"
            onPick={handleAddReaction}
            onRemove={handleRemoveReaction}
            disabled={isOptimistic}
            selectedEmojis={selectedEmojis}
            // this improves performance because it will cancel the timeout
            // if the component animates out before the timeout is reached
            initialEmojisTimeout={isPresent ? 500 : undefined}
            searchInputRef={searchInputRef}
            additionalRecentEmojis={thread.reactions
              .map(({ emoji }) => emoji)
              .toReversed()}
          />
        </div>
      )}
    </div>
  );
};

type ReactionThreadContentProps = {
  thread: ExtractReactionThreadTypes<typeof THREAD_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  expanded: boolean;
  expand: () => void;
  unresolveThread: (threadId: string) => void;
  asPopOver?: boolean;
  onResize: () => void;
  overlapped: boolean;
  deleteReactionThread: () => void;
} & Pick<
  React.ComponentProps<typeof InnerReactionThreadContent>,
  'deleteReaction' | 'addReaction'
>;

const ReactionThreadContent = ({
  expanded,
  thread,
  overlapped,
  ...props
}: ReactionThreadContentProps) => {
  const layoutId = useId();

  const containerClassName = classNames(styles.container, {
    [styles.asPopOver]: props.asPopOver,
    [styles.expanded]: expanded,
    [styles.overlapped]: overlapped,
  });

  const innerContainer = (
    <InnerReactionThreadContent
      {...props}
      thread={thread}
      expanded={expanded}
      layoutId={layoutId}
    />
  );

  return (
    <div className={styles.wrapper}>
      <MotionConfig transition={TRANSITION}>
        <AnimatePresence initial={false} mode="popLayout">
          {expanded && (
            <motion.div
              className={containerClassName}
              initial={{ y: -30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -30, opacity: 0 }}
              key="expanded"
              {...addTestId(`magnetic-wall-item-reaction-${thread.id}`)}
            >
              {innerContainer}
            </motion.div>
          )}
          {!expanded && (
            <motion.div
              className={containerClassName}
              initial={{ y: 30, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 30, opacity: 0 }}
              key="collapsed"
              {...addTestId(`magnetic-wall-item-reaction-${thread.id}`)}
            >
              {innerContainer}
            </motion.div>
          )}
        </AnimatePresence>
      </MotionConfig>
    </div>
  );
};

export default ReactionThreadContent;
